/* What we say when a resume won't read.

   Lived in two places (GetStartedNeedFirst and FinishSetupPage) as identical
   literals, and /auth/register had no mapping at all -- so the one door that
   accepted the fewest formats was also the only one that never explained a
   rejection. Somebody uploading a Pages file there got a bare form error.

   Shared rather than copied a third time: these strings are the whole point of
   returning a named reason instead of a boolean, and three copies drift.

   Every line names the specific problem AND the specific fix. "We couldn't
   read that" loses the person; for most of these the fix is thirty seconds of
   work in the app they already have the file open in. */
export const RESUME_HELP = {
  // Only fires when the olefile recovery found nothing -- an ordinary .doc reads fine now.
  legacy_doc: "We couldn't get the text out of that older .doc. Open it, choose Save As, pick PDF or .docx, and try again.",
  scanned_pdf_unreadable: "That scan came out too blurry to read. A clearer photo, or the original Word or PDF version, will work.",
  encrypted_pdf: "That PDF is password protected. Save an unlocked copy and try again.",
  corrupt_pdf: "That PDF wouldn't open. Try re-saving or exporting it, or send a Word version.",
  image_file_unreadable: "That photo came out too blurry to read. Try again in better light with the whole page in frame, or send a PDF.",
  // HEIC is converted to JPEG server-side now, so this only fires on a corrupt file.
  heic_file: "That Apple photo wouldn't open. In Photos, share it as JPEG, or send a PDF.",
  // Pages bundles carry a PDF preview we read; this is the save-without-preview case.
  pages_file: "That Pages file was saved without a preview we can read. In Pages, choose Export To, then PDF or Word.",
  unsupported_type: "We can read PDF, Word, Pages, OpenDocument, RTF, text, and photos. Export yours to PDF and it'll work.",
  too_large: "That file is over 10 MB. A PDF export is usually much smaller.",
  too_little_text: "There wasn't much text in that one, so we've kept what we could.",
  could_not_draft: "We've kept what we could from it.",
  could_not_read: "We couldn't get the text out of that one.",
  // Quota, not a file problem — never blame the file for our budget.
  draft_limit_visitor: "You've used your drafts for today.",
  draft_limit_ip: "We've hit today's limit for drafting from your network.",
};

/** The help line for a reason code, never undefined. */
export function resumeHelpFor(reason) {
  return RESUME_HELP[reason] || RESUME_HELP.could_not_read;
}

/* The `accept` attribute for every resume input.

   Carries MIME types alongside the extensions on purpose. An extension-only
   list is the version that behaves worst in the mobile pickers: iOS maps
   `accept` onto UTIs, and entries it can't map can grey out iCloud and Drive
   documents or drop the photo option entirely -- which is exactly the "it
   isn't working" people were reporting. The server sniffs content anyway, so
   this attribute is a convenience for the picker, never the real gate. */
export const RESUME_ACCEPT = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/vnd.oasis.opendocument.text",
  "application/rtf",
  "text/plain",
  "text/markdown",
  "image/*",
  ".pdf", ".docx", ".doc", ".odt", ".rtf", ".txt", ".md",
  ".png", ".jpg", ".jpeg", ".webp", ".gif", ".heic", ".heif", ".pages",
].join(",");
